/* eslint-disable no-case-declarations */
import { useReducer, useRef } from "react"
import SockJS from "sockjs-client"
import Stomp from "stompjs"
import { axiosPrivate, BASE_DOMAIN } from "../../../../../../api/axios"
import { convertDateFormatToUTCTimeStamp } from "../../../../../shared/helper/timeHelper"
import {
  getCurrentUserId,
  getNewAppointmentCount,
  getUserInfo,
  getUserRole,
  setUserInformation
} from "../../../../app/state/appSessionStorage"
import { getToken } from "../../../token/TokenEncryption"
import {
  createObjectByKey,
  resetChatState,
  updateGroupWithReceivedMessage,
  updateUserOnlineStatus
} from "../chatActions"
import {
  allowedRolesToFetchGroupData,
  ChatsActionTypes,
  IChatUIInit
} from "../ChatType"
import chatReducer from "../reducer/ChatUIReducer"
import ChatContext from "./ChatContext"
import { getCurrentUserOrgId } from "../../../../app/state/appSessionStorage"
import { showNotification } from '../../../../../../services/notificationService';

interface IProps {
  children: React.ReactNode;
}

const ChatProvider = (props: IProps): JSX.Element => {
  const [state, dispatch] = useReducer(chatReducer, IChatUIInit)
  let chatGroups: any = []


  const connect = async (userId: string, reconnectAttempt = 0) => {
    const socket = new SockJS(`${BASE_DOMAIN}/ethercms_vafa_ws`)
    const stompClient = Stomp.over(socket)

    if (allowedRolesToFetchGroupData.includes(getUserRole())) {
      const header = {
        headers: {
          Accept: "application/json, text/plain, */*",
          Authorization: `Bearer ${getToken()}`
        }
      }
      await axiosPrivate
        .get("/rest/get_chat_groups", header)
        .then((response: any) => {
          if (response.status === 200) {
            const groupsWithOrgId = response?.data?.filter((group: any) => {
              return (
                group.groupId.includes("-") &&
                group.groupId.split("-")[1] === getCurrentUserOrgId()
              )
            })
            dispatch({
              type: ChatsActionTypes.CHAT_GROUPS_LIST,
              payload: createObjectByKey(groupsWithOrgId, "groupId")
            })
            chatGroups = groupsWithOrgId
          }
        })
        .catch((error) => {
          console.error(error)
        })
    }
    
    const onPrivateMessage = (msg: any) => {
      const privateMessage = JSON.parse(msg.body)
      const { mId, sentDatetime, fromName, ...messageObj } = privateMessage
      if (privateMessage.fromId !== getCurrentUserId())
      {
        const title = mId == "APPOINTMENT" ? 'New appointment' : "New message from " + fromName;
        showNotification(privateMessage.text, title);
      }

      switch (mId) {
        case "CHAT":
          dispatch({
            type: ChatsActionTypes.RECEIVED_NEW_MESSAGE,
            payload: {
              sentDatetime: convertDateFormatToUTCTimeStamp(sentDatetime),
              ...messageObj
            }
          })
          break
        case "APPOINTMENT":
          const updatedCount = getNewAppointmentCount() + 1
          const userInfo = getUserInfo()
          userInfo.newAppointmentCount = updatedCount
          setUserInformation(userInfo)

          dispatch({
            type: ChatsActionTypes.UPDATE_NEW_APPOINTMENT_COUNT,
            payload: updatedCount
          })
          break
      }
    }

    const onUserOnline = (msg: any) => {
      const m = JSON.parse(msg.body)
      dispatch(updateUserOnlineStatus(m))
    }

    const onGroupMessage = (msg: any) => {
      const m = JSON.parse(msg.body)

      if (m.fromId !== getCurrentUserId()) {
        showNotification(m.text, "New group message in " + m.fromName);
        dispatch(updateGroupWithReceivedMessage(m))
      }
    }

    const onConnected = async () => {
      dispatch({
        type: ChatsActionTypes.SET_SOCKET,
        payload: stompClient
      })
      await stompClient?.send(
        "/app/chat-add",
        {},
        JSON.stringify({
          senderId: userId,
          datetime: new Date()
        })
      )
      
      const groupSubscriptions: any = []
      const userIdSub = stompClient?.subscribe(
        `/topic/${userId}`,
        onPrivateMessage
      )
      const userOnlineSub = stompClient?.subscribe(
        "/topic/public",
        onUserOnline
      )

      chatGroups.map((group: any) => {
        const groupSubscription = stompClient?.subscribe(
          `/topic/${group.groupId}`,
          onGroupMessage
        )
        groupSubscriptions.push(groupSubscription)
      })
    }

    const onDisconnect = (err: any) => {
      dispatch({
        type: ChatsActionTypes.SET_SOCKET,
        payload: null
      })
    }

    try {
      if (userId) {
        stompClient.connect({}, onConnected, onDisconnect);
      }
    } catch (err) {
      console.error('Connection Error:', err);
    }
  }

  const disconnect = () => {
    if (state.socket !== null) {
      state.socket.disconnect(() => {
        dispatch({
          type: ChatsActionTypes.SET_SOCKET,
          payload: null
        })
      })
    }
    dispatch(resetChatState(IChatUIInit))
  }

  const providerState = {
    state,
    dispatch,
    connect,
    disconnect
  }

  return (
    <ChatContext.Provider value={providerState}>
      {props.children}
    </ChatContext.Provider>
  )
}

export default ChatProvider
